/** TRBUD-343: saved definitions contain governed IDs, never SQL. */
import { z } from "zod";
import { strictObject } from "./requests.js";

export const MATRIX_BASE_IDS = ["assets", "work_orders", "vendors", "parts", "vendor_invoices", "money_log", "time_log", "downtime_log", "parts_log", "po_variance", "stage_history"] as const;
export type MatrixBaseId = (typeof MATRIX_BASE_IDS)[number];
export const MATRIX_AGGREGATES = ["sum", "count", "count_distinct", "avg", "count_where", "ratio"] as const;
export type MatrixAggregate = (typeof MATRIX_AGGREGATES)[number];
export type MatrixType = "text" | "number" | "date" | "timestamp" | "boolean";
export type MatrixFormat = "money" | "hours" | "rate" | "work_order" | "number";
export type MatrixScalar = string | number | boolean | null;
export interface MatrixPath { id: string; view: string; localKey: string; foreignKey: string; parentPath?: string }
export interface MatrixField {
  id: string; label: string; type: MatrixType; column: string; path?: string;
  aggregates: readonly MatrixAggregate[]; seeCosts?: boolean;
  format?: MatrixFormat;
  flow?: { view: string; keyColumn: string; valueColumn: string; dateColumn: string; filters?: readonly { column: string; value: string | number | boolean }[] };
  ratio?: { numerator: string; denominator: string };
  many?: { view: string; baseKeyColumn: string; valueColumn: string; labelColumn: string; weightColumn: string };
}
export interface MatrixBase {
  id: MatrixBaseId; label: string; view: string; keyColumn: string;
  timeframes: readonly { id: string; label: string; column: string }[];
  defaultTimeBasis?: string; fields: readonly MatrixField[]; paths: readonly MatrixPath[];
  costScope?: { kind: "all" | "employee"; userColumn?: string }; notes?: readonly string[];
}
const scalar = z.union([z.string().max(500), z.number().finite(), z.boolean(), z.null()]);
const fieldId = z.string().min(1).max(180).regex(/^[a-z][a-z0-9_.]*$/);
const filter = strictObject({
  field: fieldId,
  operator: z.enum(["eq", "in", "range", "is_null", "not_null", "older_than_days"]),
  value: z.union([scalar, z.array(scalar).min(1).max(100)]).optional(),
  from: scalar.optional(), to: scalar.optional(),
}).superRefine((value, ctx) => {
  if (["eq", "in", "older_than_days"].includes(value.operator) && value.value === undefined)
    ctx.addIssue({ code: "custom", message: "This filter requires a value" });
  if (value.operator === "in" && !Array.isArray(value.value))
    ctx.addIssue({ code: "custom", message: "In requires a list" });
  if (["eq", "older_than_days"].includes(value.operator) && Array.isArray(value.value))
    ctx.addIssue({ code: "custom", message: "This filter requires a scalar value" });
  if (value.operator !== "range" && (value.from !== undefined || value.to !== undefined))
    ctx.addIssue({ code: "custom", message: "Only range accepts boundaries" });
  if (["range", "is_null", "not_null"].includes(value.operator) && value.value !== undefined)
    ctx.addIssue({ code: "custom", message: "This filter does not accept a value" });
  if (value.operator === "range" && value.from === undefined && value.to === undefined)
    ctx.addIssue({ code: "custom", message: "Range requires a boundary" });
});
export const matrixDefinitionBody = strictObject({
  version: z.literal(2), base: z.enum(MATRIX_BASE_IDS), mode: z.enum(["detail", "grouped"]),
  columns: z.array(strictObject({ field: fieldId, aggregate: z.enum(MATRIX_AGGREGATES).optional(), bucket: z.enum(["day", "week", "month", "year"]).optional() })).min(1).max(30),
  filters: z.array(filter).max(30).default([]),
  timeframe: z.enum(["all", "30d", "quarter", "ytd"]).default("30d"),
  timeBasis: z.string().min(1).max(80).regex(/^[a-z][a-z0-9_]*$/).optional(),
  shareMode: z.enum(["full", "shared"]).default("full"),
  sort: z.array(strictObject({ column: z.number().int().min(0).max(29), direction: z.enum(["asc", "desc"]) })).max(5).default([]),
  topN: z.number().int().min(1).max(100000).optional(),
  chart: strictObject({ kind: z.enum(["table", "bar", "stacked_bar", "line", "kpi"]), x: z.number().int().min(0).max(29).optional(), values: z.array(z.number().int().min(0).max(29)).min(1).max(8), series: z.number().int().min(0).max(29).optional() }).optional(),
}).superRefine((definition, ctx) => {
  if (definition.mode === "detail" && definition.columns.some((column) => column.aggregate))
    ctx.addIssue({ code: "custom", message: "Detail columns cannot aggregate" });
  const indices = [...definition.sort.map((sort) => sort.column), ...(definition.chart?.values ?? []), definition.chart?.x, definition.chart?.series].filter((index): index is number => index !== undefined);
  if (indices.some((index) => index >= definition.columns.length))
    ctx.addIssue({ code: "custom", message: "Sort/chart references a missing column" });
});
export type MatrixDefinition = z.infer<typeof matrixDefinitionBody>;
export const runMatrixBody = strictObject({ definition: matrixDefinitionBody });
export const exportMatrixBody = strictObject({ definition: matrixDefinitionBody, definitionId: z.string().uuid().optional() });
export const saveMatrixBody = strictObject({
  name: z.string().trim().min(1).max(160), description: z.string().trim().max(1000).optional(),
  definition: matrixDefinitionBody, isBuiltin: z.boolean().default(false),
  replacesId: z.string().uuid().optional(), idempotencyKey: z.string().min(1).max(200),
});
export const retireMatrixBody = strictObject({ reason: z.string().trim().min(1).max(1000), idempotencyKey: z.string().min(1).max(200) });
export interface MatrixColumn { key: string; label: string; type: MatrixType; aggregate?: MatrixAggregate; numeratorKey?: string; denominatorKey?: string; format?: MatrixFormat }
export interface MatrixRunResponse {
  definition: MatrixDefinition; columns: MatrixColumn[]; rows: Record<string, MatrixScalar>[];
  totalRows: number; truncated: boolean; totals: Record<string, MatrixScalar>;
  deduplicatedTotals?: Record<string, MatrixScalar>; chartRows?: Record<string, MatrixScalar>[];
  notes: string[]; question: string; window: { from: string | null; to: string };
  currency?: string | null;
}
export interface SavedMatrixDefinition {
  id: string; slug: string; name: string; description: string | null; isBuiltin: boolean;
  createdByUserId: string | null; createdAt: string; definition: MatrixDefinition | null;
  needsConversion: boolean;
}
export interface MatrixDefinitionsResponse { definitions: SavedMatrixDefinition[] }
export interface MatrixRegistryResponse { bases: readonly MatrixBase[] }
